import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function PublicTeams() {
  const { t: tr } = useTranslation();
  const [teams, setTeams] = useState([]);
  const [tournaments, setTournaments] = useState({});
  const [tournamentsList, setTournamentsList] = useState([]);
  const [tournament, setTournament] = useState("all");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/teams"), api.get("/tournaments")]).then(([t, tt]) => {
      setTeams(t.data);
      setTournamentsList(tt.data);
      setTournaments(Object.fromEntries(tt.data.map((x) => [x.id, x])));
    });
  }, []);

  const selectedTournament = tournamentsList.find((tt) => tt.id === tournament);
  const categoryOptions = selectedTournament?.categories || [];
  useEffect(() => { setCategory("all"); }, [tournament]);

  const filtered = useMemo(() => teams.filter((team) => {
    const teamTids = (team.tournament_ids && team.tournament_ids.length ? team.tournament_ids : (team.tournament_id ? [team.tournament_id] : []));
    if (tournament !== "all" && !teamTids.includes(tournament)) return false;
    if (category !== "all") {
      const cbt = team.category_by_tournament || {};
      // If a tournament is chosen, category must match within that tournament; otherwise match any tournament
      if (tournament !== "all") {
        if ((cbt[tournament] || (tournament === team.tournament_id ? team.category_id : null)) !== category) return false;
      } else {
        const anyMatch = Object.values(cbt).includes(category) || (Object.keys(cbt).length === 0 && team.category_id === category);
        if (!anyMatch) return false;
      }
    }
    return true;
  }), [teams, tournament, category]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <PageHeader title={tr("teams.title")} subtitle={tr("teams.subtitle")} testid="page-teams" />
      <div className={`grid ${categoryOptions.length > 0 ? "md:grid-cols-2" : "md:grid-cols-1 max-w-md"} gap-3 mb-6`}>
        <Select value={tournament} onValueChange={setTournament}>
          <SelectTrigger data-testid="teams-tournament-filter" className="bg-navy-deep border-navy-border text-white rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-navy-deep border-navy-border text-white">
            <SelectItem value="all">{tr("games.filter_tournament_all")}</SelectItem>
            {tournamentsList.map((tour) => <SelectItem key={tour.id} value={tour.id}>{tour.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {categoryOptions.length > 0 && (
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="teams-category-filter" className="bg-navy-deep border-navy-border text-white rounded-sm">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent className="bg-navy-deep border-navy-border text-white">
              <SelectItem value="all">Todas as categorias</SelectItem>
              {categoryOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {filtered.length === 0 ? (
        <div className="border border-dashed border-navy-border p-12 text-center text-ink-muted">{tr("teams.empty")}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((team) => {
            const teamTids = (team.tournament_ids && team.tournament_ids.length ? team.tournament_ids : (team.tournament_id ? [team.tournament_id] : []));
            const tourNames = teamTids.map((id) => tournaments[id]?.name).filter(Boolean);
            const cbt = team.category_by_tournament || {};
            // Category badge: prefer per-tournament (if a specific tournament is filtered); otherwise show first category found
            const activeCatId = tournament !== "all" ? (cbt[tournament] || (tournament === team.tournament_id ? team.category_id : null)) : (Object.values(cbt)[0] || team.category_id);
            const activeCatTid = tournament !== "all" ? tournament : (Object.keys(cbt)[0] || team.tournament_id);
            const cat = (tournaments[activeCatTid]?.categories || []).find((c) => c.id === activeCatId);
            return (
              <Link
                key={team.id}
                to={`/teams/${team.id}`}
                data-testid={`team-card-${team.id}`}
                className="group flex items-center gap-4 p-5 bg-navy-deep border border-navy-border rounded-sm hover:border-orange transition-all"
              >
                <div className="w-16 h-16 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden">
                  {team.logo_url ? <img src={fileUrl(team.logo_url)} alt="" className="w-full h-full object-cover" />
                    : <span className="font-heading font-black text-3xl text-orange">{team.name?.[0]}</span>}
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-bold uppercase tracking-tight text-xl text-white group-hover:text-orange truncate">{team.name}</div>
                  <div className="text-xs font-mono uppercase tracking-widest text-ink-muted truncate">{tourNames.join(" · ") || "—"}</div>
                  {cat && <div className="mt-1 inline-block px-1.5 py-0.5 text-[10px] font-heading uppercase tracking-widest bg-orange/20 text-orange rounded-sm">{cat.name}</div>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
